<script lang="ts">
	import { onMount } from 'svelte';
	import { DEMO_ARTICLE, DEMO_FILE_NAME } from '#lib/demo';
	import { documentFromText } from '#lib/extract/plain';
	import { analyzeDocument, type AnalysisResult } from '#lib/report/engine';
	import type { ExtractedDocument } from '#lib/types';
	import { makeSnapshot, saveSnapshot } from '#lib/history/storage';
	import type { HistorySnapshot } from '#lib/history/types';
	import { analyzeFile, type PipelineProgress } from '#lib/pipeline';
	import { toRepError, RepError, type RepError as RepErrorType } from '#lib/errors';
	import { t } from '#lib/i18n/state.svelte';
	import { formatCount } from '#lib/ui/labels';
	import Dropzone from '#lib/components/Dropzone.svelte';
	import Processing from '#lib/components/Processing.svelte';
	import ReportView from '#lib/components/ReportView.svelte';
	import TestSetPanel from '#lib/components/TestSetPanel.svelte';
	import LangToggle from '#lib/components/LangToggle.svelte';
	import BatchUpload from '#lib/components/BatchUpload.svelte';
	import BatchProgress from '#lib/components/BatchProgress.svelte';
	import BatchSummary from '#lib/components/BatchSummary.svelte';
	import JournalPicker from '#lib/components/JournalPicker.svelte';
	import UpgradeModal from '#lib/components/UpgradeModal.svelte';
	import { authState, canAnalyzeNow, ensureProductAccess, fetchUser, logout } from '#lib/auth/store.svelte';
	import PreviewSummary from '#lib/components/PreviewSummary.svelte';
 import { projectStatistics } from '#lib/analytics/schema';
 import { recordStatistics } from '#lib/analytics/client';
 import { refreshUsage } from '#lib/auth/store.svelte';
	import type { BatchHistoryEntry, BatchItem } from '#lib/batch/types';
	import { batchRows, batchSignature } from '#lib/batch/types';
	import { findPreviousBatch, saveBatch } from '#lib/batch/history';

	type View = 'landing' | 'processing' | 'report' | 'error';

	const isDev = import.meta.env.DEV;

	let view = $state<View>('landing');
	let progress = $state<PipelineProgress>({ stageKey: 'proc.stage.starting', ratio: 0, phase: 'extract' });
	let fileName = $state('');
	let error = $state<RepErrorType | null>(null);
	let analysis = $state<AnalysisResult | null>(null);
	let snapshot = $state<HistorySnapshot | null>(null);
	let doc = $state<ExtractedDocument | null>(null);
	let pasteOpen = $state(false);
	let pasteText = $state('');
	let isDemo = $state(false);

	let mode = $state<'single' | 'batch'>('single');
	let batchStage = $state<'upload' | 'progress' | 'summary'>('upload');
	let batchItems = $state.raw<BatchItem[]>([]);
	let batchEntry = $state<BatchHistoryEntry | null>(null);
	let batchPrevious = $state<BatchHistoryEntry | null>(null);
	let batchCancelled = $state(false);
	let fromBatch = $state(false);
	let journalId = $state<string | null>(null);
	let limitOpen = $state(false);
	let sampleTab = $state<'finding' | 'source' | 'explanation'>('finding');
	const auth = authState();
 let authOpening=$state(false);
 let quotaBlocked=$state(false);
 let statisticsMeta=$state<{id:string;durationMs:number;fileSize:number;source:'file'|'paste'|'demo'}|null>(null);
 const sentStatistics=new Set<string>();
 const fullVisible=$derived(Boolean(auth.user&&auth.access.allowed&&!quotaBlocked));
 onMount(()=>{
  let checking=false;
  const recheck=async()=>{if(checking||document.visibilityState!=='visible')return;checking=true;try{await fetchUser();}finally{checking=false;}};
  const timer=setInterval(()=>{if(analysis||authOpening||mode==='batch')void recheck();},30000);
  document.addEventListener('visibilitychange',recheck);
  window.addEventListener('focus',recheck);
  return ()=>{clearInterval(timer);document.removeEventListener('visibilitychange',recheck);window.removeEventListener('focus',recheck);};
 });
 $effect(()=>{
  if(analysis&&statisticsMeta){
   const current=analysis.report,meta=statisticsMeta;
   const mode=fullVisible?'full':'preview';
   const key=meta.id+':'+mode;
   if(!sentStatistics.has(key)){
    sentStatistics.add(key);
    void recordStatistics(projectStatistics(current,{analysisId:meta.id,mode,source:meta.source,fileSize:meta.fileSize,durationMs:meta.durationMs})).then(result=>{
     if(statisticsMeta?.id!==meta.id)return;
     if(result.status===402){quotaBlocked=true;limitOpen=true;}
     if(result.used!==undefined)refreshUsage(result.used);
    });
   }
  }
 });

	const checks = $derived([
		{ key: 'stat', icon: 'Σ', title: t('checks.stat.title'), body: t('checks.stat.body'), detail: t('checks.stat.detail'), weight: 33 },
		{ key: 'cite', icon: '”', title: t('checks.cite.title'), body: t('checks.cite.body'), detail: t('checks.cite.detail'), weight: 24 },
		{ key: 'struct', icon: '¶', title: t('checks.struct.title'), body: t('checks.struct.body'), detail: t('checks.struct.detail'), weight: 19 },
		{ key: 'lang', icon: '✎', title: t('checks.lang.title'), body: t('checks.lang.body'), detail: t('checks.lang.detail'), weight: 14 },
		{ key: 'access', icon: '◉', title: t('checks.access.title'), body: t('checks.access.body'), detail: t('checks.access.detail'), weight: 5 },
		{ key: 'prov', icon: '⌗', title: t('checks.prov.title'), body: t('checks.prov.body'), detail: t('checks.prov.detail'), weight: 5 }
	]);

	const phase = $derived(view);
	const errorText = $derived(error ? t(error.code, error.params) : '');

 function record(a:AnalysisResult,durationMs=0,source:'file'|'paste'|'demo'='file',id:string=crypto.randomUUID()){
  const snap=makeSnapshot(a.report);
  snapshot=snap;saveSnapshot(snap);
  quotaBlocked=false;
  statisticsMeta={id,durationMs,fileSize:doc?.fileSize??0,source};
 }

	function batchById(id: string): BatchItem | undefined {
		return batchItems.find((i) => i.id === id);
	}

	function updateBatch(id: string, patch: Partial<BatchItem>) {
		batchItems = batchItems.map((i) => (i.id === id ? { ...i, ...patch } : i));
	}

	async function startBatch(files: File[]) {
		if (!await ensureProductAccess({redirect:false})) { authOpening=true; return; }
		batchCancelled = false;
		batchEntry = null;
		batchStage = 'progress';
		batchItems = files.map((file) => ({
			id: crypto.randomUUID(),
			file,
			name: file.name,
			size: file.size,
			status: 'queued',
			ratio: 0,
			stageKey: 'batch.status.queued',
			cancelled: false
		}));

		const signature = batchSignature(files);
		batchPrevious = findPreviousBatch(signature);

		const cores =
			typeof navigator !== 'undefined' && navigator.hardwareConcurrency
				? navigator.hardwareConcurrency
				: 4;
		const limit = Math.min(3, Math.max(1, Math.floor(cores / 2)));
		let cursor = 0;

		const worker = async () => {
			while (true) {
				if (batchCancelled) return;
				const index = cursor++;
				if (index >= files.length) return;
				const item = batchItems[index];
				if (!item || item.cancelled) continue;
				if (!await ensureProductAccess({redirect:false})) { batchCancelled=true; return; }
				const id = item.id;
				updateBatch(id, { status: 'extracting', stageKey: 'proc.stage.preparing', ratio: 0.02 });
				try {
					const started=performance.now();
					const result = await analyzeFile(item.file, (p) => {
						const current = batchById(id);
						if (!current || current.cancelled || batchCancelled) return;
						updateBatch(id, {
							status: p.phase === 'extract' ? 'extracting' : 'analyzing',
							ratio: p.ratio,
							stageKey: p.stageKey
						});
					});
					const key=id+':full';
     if(!sentStatistics.has(key)){sentStatistics.add(key);void recordStatistics(projectStatistics(result.analysis.report,{analysisId:id,mode:'full',source:'file',fileSize:item.file.size,durationMs:performance.now()-started})).then(r=>{if(r.used!==undefined)refreshUsage(r.used);});}
     const current = batchById(id);
     if (!current || current.cancelled || batchCancelled) {
						updateBatch(id, { status: 'cancelled', cancelled: true, ratio: 1 });
						continue;
					}
					updateBatch(id, {
						status: 'done',
						ratio: 1,
						analysis: result.analysis,
						doc: result.doc
					});
				} catch (err) {
					const e = toRepError(err);
					updateBatch(id, { status: 'error', error: e.code, ratio: 1 });
				}
			}
		};

		await Promise.all(
			Array.from({ length: Math.min(limit, files.length) }, () => worker())
		);
		batchStage = 'summary';
		const rows = batchRows(batchItems);
		batchEntry = saveBatch(signature, rows);
	}

	function cancelBatchItem(id: string) {
		const item = batchById(id);
		if (!item || item.status === 'done' || item.status === 'error') return;
		updateBatch(id, {
			cancelled: true,
			status: item.status === 'queued' ? 'cancelled' : item.status
		});
	}

	function cancelBatchAll() {
		batchCancelled = true;
		batchItems = batchItems.map((item) =>
			item.status === 'done' || item.status === 'error'
				? item
				: {
						...item,
						cancelled: true,
						status: item.status === 'queued' ? ('cancelled' as const) : item.status
					}
		);
	}

	function openBatchDetail(index: number) {
		const item = batchItems[index];
		if (!item?.analysis || !item.doc) return;
		analysis = item.analysis;
		doc = item.doc;
		record(item.analysis,0,'file',item.id);
		fileName = item.name;
		isDemo = false;
		fromBatch = true;
		view = 'report';
		window.scrollTo({ top: 0, behavior: 'instant' });
	}

	function backFromReport() {
		if (fromBatch) {
			fromBatch = false;
			view = 'landing';
			mode = 'batch';
			batchStage = 'summary';
			analysis = null;
			doc = null;
			snapshot = null;
			window.scrollTo({ top: 0, behavior: 'instant' });
			return;
		}
		reset();
	}

	function newBatch() {
		batchItems = [];
		batchEntry = null;
		batchPrevious = null;
		batchStage = 'upload';
		batchCancelled = false;
	}

	async function handleFile(file: File) {
 const started=performance.now();

		if (!canAnalyzeNow()) {
			limitOpen = true;
			return;
		}
		fileName = file.name;
		isDemo = false;
		view = 'processing';
		error = null;
		progress = { stageKey: 'proc.stage.preparing', ratio: 0, phase: 'extract' };
		try {
			const result = await analyzeFile(file, (p) => (progress = p));
			analysis = result.analysis;
			doc = result.doc;
			record(result.analysis,performance.now()-started);
			view = 'report';
			window.scrollTo({ top: 0, behavior: 'instant' });
		} catch (err) {
			error = toRepError(err);
			view = 'error';
		}
	}

	async function loadDemo() {
 const started=performance.now();

		fileName = DEMO_FILE_NAME;
		isDemo = true;
		view = 'processing';
		error = null;
		progress = { stageKey: 'proc.stage.demoPreparing', ratio: 0.15, phase: 'extract' };
		setTimeout(() => {
			try {
				const d = documentFromText(DEMO_ARTICLE, DEMO_FILE_NAME, 'txt');
				progress = { stageKey: 'proc.stage.analyzing', ratio: 0.7, phase: 'analyze' };
				const a = analyzeDocument(d);
				analysis = a;
				doc = d;
				record(a,performance.now()-started,'demo');
				view = 'report';
				window.scrollTo({ top: 0, behavior: 'instant' });
			} catch (err) {
				error = toRepError(err);
				view = 'error';
			}
		}, 220);
	}

	async function analyzePasted() {
 const started=performance.now();

		if (!canAnalyzeNow()) {
			limitOpen = true;
			return;
		}
		if (pasteText.trim().length < 100) {
			error = new RepError('err.tooShort');
			return;
		}
		pasteOpen = false;
		isDemo = false;
		fileName = t('paste.fileName');
		view = 'processing';
		progress = { stageKey: 'proc.stage.analyzing', ratio: 0.5, phase: 'analyze' };
		setTimeout(() => {
			try {
				const d = documentFromText(pasteText, t('paste.fileNameTxt'), 'txt');
				analysis = analyzeDocument(d);
				doc = d;
				record(analysis,performance.now()-started,'paste');
				view = 'report';
				window.scrollTo({ top: 0, behavior: 'instant' });
			} catch (err) {
				error = toRepError(err);
				view = 'error';
			}
		}, 180);
	}

	function reset() {
  statisticsMeta=null;quotaBlocked=false;authOpening=false;
		view = 'landing';
		analysis = null;
		snapshot = null;
		doc = null;
		error = null;
		fileName = '';
		pasteText = '';
		isDemo = false;
		mode = 'single';
		batchStage = 'upload';
		batchItems = [];
		batchEntry = null;
		batchPrevious = null;
		batchCancelled = false;
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}

	function retry() {
		view = 'landing';
		error = null;
	}
</script>

<svelte:head>
	<title>{t('meta.title')}</title>
</svelte:head>

{#if phase === 'report' && analysis && doc}
	{#if fullVisible}
  <ReportView {analysis} {doc} onreset={backFromReport} {isDemo} {snapshot} {journalId} />
 {:else}
  <PreviewSummary report={analysis.report} onreset={backFromReport} onlogin={()=>{authOpening=true;}} waiting={authOpening} quota={quotaBlocked} />
 {/if}
{:else}
	<div class="shell">
		<header class="topbar">
			<a class="brand" href="/" aria-label={t('brand.homeAria')} onclick={(e) => { e.preventDefault(); reset(); }}>
				<span class="logo" aria-hidden="true">R<span class="logo-dot"></span></span>
				<span class="brand-name">RepCheck</span>
			</a>
			<nav class="top-nav" aria-label={t('nav.checks')}>
				<a href="#checks">{t('nav.checks')}</a>
				<a href="#privacy">{t('nav.privacy')}</a>
				<a href="#method">{t('nav.method')}</a>
			</nav>
			<div class="top-actions">
				<LangToggle compact />
				{#if auth.user}
					<span class="auth-user" title={auth.user.email}>
						{auth.user.email}
						<span class="plan">{t(auth.user.institutionId?'auth.plan.institution':auth.user.plan === 'pro' ? 'auth.plan.pro' : 'auth.plan.free')}</span>
					</span>
					<button class="btn btn-ghost btn-sm" onclick={() => void logout()}>{t('auth.signOut')}</button>
				{:else}
					<a class="btn btn-sm" href="/auth/login">{t('auth.signIn')}</a>
				{/if}
				{#if isDev}
					<a class="btn btn-sm" href="#test-set">{t('nav.testSet')}</a>
				{/if}
			</div>
		</header>

		<main class="main">
			{#if mode === 'batch' && !auth.access.allowed}
    <section class="batch-view"><div class="upload-card" style="padding:2rem">
     <h1>{t('preview.batchTitle')}</h1><p>{t('preview.batchBody')}</p>
     <a class="btn btn-primary" href="/api/auth/login/libedge?flow=preview" target="_blank" rel="noopener" onclick={()=>{authOpening=true;}}>{t('auth.libedge')}</a>
     <button class="btn btn-ghost" onclick={()=>{mode='single';}}>{t('upload.single')}</button>
     {#if authOpening}<p role="status">{t('preview.waiting')}</p>{/if}
    </div></section>
   {:else if mode === 'batch'}
    <section class="batch-view">
					<div class="upload-tabs" role="tablist" aria-label={t('batch.modeAria')}>
						<button
							class="upload-tab"
							role="tab"
							aria-selected={false}
							onclick={() => (mode = 'single')}
						>
							{t('upload.single')}
						</button>
						<button class="upload-tab active" role="tab" aria-selected={true} onclick={() => (mode = 'batch')}>
							{t('upload.batch')}
						</button>
					</div>
					<div class="batch-panel">
						<div class="journal-row">
							<JournalPicker value={journalId} onchange={(id) => (journalId = id)} />
						</div>
						{#if batchStage === 'upload'}
							<BatchUpload onstart={startBatch} max={auth.user?.plan === 'pro' ? 20 : 5} />
						{:else if batchStage === 'progress'}
							<BatchProgress items={batchItems} oncancelall={cancelBatchAll} oncancelitem={cancelBatchItem} />
						{:else}
							<BatchSummary
								items={batchItems}
								entry={batchEntry}
								previous={batchPrevious}
								ondetail={openBatchDetail}
								onnew={newBatch}
							/>
						{/if}
					</div>
				</section>
			{:else}
				<section class="hero" aria-labelledby="hero-title">
					<div class="hero-copy">
						<p class="eyebrow">{t('hero.kicker')}</p>
						<h1 id="hero-title">{t('hero.title')}</h1>
						<p class="lede">{t('hero.lede')}</p>

						<div class="privacy-note">
							<span class="privacy-icon" aria-hidden="true">
								<svg viewBox="0 0 24 24" width="22" height="22">
									<rect
										x="5"
										y="10"
										width="14"
										height="11"
										rx="2.5"
										fill="none"
										stroke="currentColor"
										stroke-width="1.8"
									/>
									<path
										d="M8 10V7.5a4 4 0 0 1 8 0V10"
										fill="none"
										stroke="currentColor"
										stroke-width="1.8"
										stroke-linecap="round"
									/>
									<circle cx="12" cy="15.5" r="1.2" fill="currentColor" />
								</svg>
							</span>
							<div>
								<strong>{t('hero.privacyTitle')}</strong>
								<span>{t('hero.privacyBody')}</span>
							</div>
						</div>

						<div class="upload-card">
							<div class="upload-tabs" role="tablist" aria-label={t('batch.modeAria')}>
								<button
									class="upload-tab active"
									role="tab"
									aria-selected={true}
									onclick={() => (mode = 'single')}
								>
									{t('upload.single')}
								</button>
								<button
									class="upload-tab"
									role="tab"
									aria-selected={false}
									onclick={() => (mode = 'batch')}
								>
									{t('upload.batch')}
								</button>
							</div>
							<div class="upload-body">
								<Dropzone onfile={handleFile} disabled={phase === 'processing'} />
								<button class="paste-link" onclick={() => (pasteOpen = !pasteOpen)}>
									{t('upload.paste')}
								</button>
								<details class="journal-details">
									<summary>
										<span>{t('upload.targetJournal')}</span>
										<span aria-hidden="true">⌄</span>
									</summary>
									<JournalPicker value={journalId} onchange={(id) => (journalId = id)} />
								</details>
							</div>
						</div>

						<button
							type="button"
							class="sample-cta"
							onclick={loadDemo}
							disabled={phase === 'processing'}
						>
							<span class="sample-cta-icon" aria-hidden="true">📊</span>
							<span>
								<strong>{t('sample.cta')}</strong>
								<small>{t('sample.cta.sub')}</small>
							</span>
							<span class="sample-cta-arrow" aria-hidden="true">→</span>
						</button>

						{#if view === 'processing'}
							<div class="processing-wrap">
								<Processing {fileName} {progress} />
							</div>
						{/if}

						{#if view === 'error'}
							<div class="error-card card" role="alert">
								<h2>{t('error.title')}</h2>
								<p>{errorText}</p>
								<div class="row">
									<button class="btn" onclick={retry}>{t('error.retry')}</button>
									<button class="btn btn-ghost" onclick={loadDemo}>{t('error.demo')}</button>
								</div>
							</div>
						{/if}

						{#if pasteOpen}
							<div class="paste card">
								<div class="row-between">
									<h2>{t('paste.title')}</h2>
									<button
										class="btn btn-ghost btn-sm"
										onclick={() => (pasteOpen = false)}
										aria-label={t('common.close')}
									>
										✕
									</button>
								</div>
								<p class="small muted">{t('paste.hint')}</p>
								<textarea
									bind:value={pasteText}
									rows="10"
									placeholder={t('paste.placeholder')}
								></textarea>
								<div class="row-between">
									<span class="small muted">{t('paste.chars', { n: formatCount(pasteText.length) })}</span>
									<button class="btn btn-primary" onclick={analyzePasted} disabled={pasteText.trim().length < 100}>
										{t('paste.analyze')}
									</button>
								</div>
							</div>
						{/if}
					</div>

					<aside class="sample-report card" aria-labelledby="sample-report-title">
						<p class="sample-kicker">{t('sample.kicker')}</p>
						<h2 id="sample-report-title">{t('sample.cardTitle')}</h2>

						<div class="sample-tabs" role="tablist" aria-label={t('sample.cardTitle')}>
							<button
								class="sample-tab"
								class:active={sampleTab === 'finding'}
								role="tab"
								aria-selected={sampleTab === 'finding'}
								onclick={() => (sampleTab = 'finding')}
							>
								{t('sample.tabFinding')}
							</button>
							<button
								class="sample-tab"
								class:active={sampleTab === 'source'}
								role="tab"
								aria-selected={sampleTab === 'source'}
								onclick={() => (sampleTab = 'source')}
							>
								{t('sample.tabSource')}
							</button>
							<button
								class="sample-tab"
								class:active={sampleTab === 'explanation'}
								role="tab"
								aria-selected={sampleTab === 'explanation'}
								onclick={() => (sampleTab = 'explanation')}
							>
								{t('sample.tabExplanation')}
							</button>
						</div>

						{#if sampleTab === 'finding'}
							<div class="finding-alert">
								<span class="finding-icon" aria-hidden="true">!</span>
								<strong>{t('hero.sampleIssue')}</strong>
								<span class="finding-confidence">{t('confidence.high')}</span>
							</div>

							<p class="sample-label">{t('sample.excerpt')}</p>
							<div class="sample-excerpt mono">t(48) = 2.41, p = <mark>.12</mark></div>

							<div class="p-compare">
								<div>
									<span>{t('sample.reportedP')}</span>
									<strong>.12</strong>
								</div>
								<div>
									<span>{t('sample.recomputedP')}</span>
									<strong>≈ .020</strong>
								</div>
							</div>
						{:else if sampleTab === 'source'}
							<p class="sample-label">{t('sample.excerpt')}</p>
							<div class="sample-excerpt mono">t(48) = 2.41, p = <mark>.12</mark></div>

							<div class="p-compare">
								<div>
									<span>{t('sample.reportedP')}</span>
									<strong>.12</strong>
								</div>
								<div>
									<span>{t('sample.recomputedP')}</span>
									<strong>≈ .020</strong>
								</div>
							</div>

							<div class="evidence-box">
								<h3>{t('sample.evidenceTitle')}</h3>
								<p>{t('sample.evidence')}</p>
							</div>
						{:else}
							<div class="sample-explanation">
								<h3>{t('sample.explanationTitle')}</h3>
								<p>{t('sample.explanation')}</p>
								<button class="source-link" onclick={() => (sampleTab = 'source')}>
									{t('sample.viewSource')} <span aria-hidden="true">→</span>
								</button>
							</div>
						{/if}

						<p class="sample-note">{t('sample.note')}</p>
					</aside>
				</section>

				<section class="metrics" aria-label={t('metrics.label')}>
					<ul class="metric-row">
						<li class="metric">
							<span class="metric-num">500+</span>
							<span class="metric-text">{t('metrics.checks')}</span>
						</li>
						<li class="metric">
							<span class="metric-num">6</span>
							<span class="metric-text">{t('metrics.engines')}</span>
						</li>
						<li class="metric">
							<span class="metric-num">100%</span>
							<span class="metric-text">{t('metrics.local')}</span>
						</li>
						<li class="metric">
							<span class="metric-num">0</span>
							<span class="metric-text">{t('metrics.upload')}</span>
						</li>
					</ul>
				</section>

				<section class="checks" id="checks" aria-labelledby="checks-title">
					<div class="section-head">
						<h2 id="checks-title">{t('checks.title')}</h2>
					</div>
					<ul class="check-grid">
						{#each checks as c (c.key)}
							<li class="check card">
								<span class="check-icon" aria-hidden="true">{c.icon}</span>
								<div>
									<h3>{c.title}</h3>
									<p>{c.body}</p>
									{#if c.detail}
										<p class="check-detail">{c.detail}</p>
									{/if}
								</div>
							</li>
						{/each}
					</ul>
				</section>

				<section class="why" aria-labelledby="why-title">
					<h2 id="why-title">{t('why.title')}</h2>
					<div class="why-grid">
						<div class="why-card">
							<span class="why-icon" aria-hidden="true">🔒</span>
							<h3>{t('why.privacy.title')}</h3>
							<p>{t('why.privacy.body')}</p>
						</div>
						<div class="why-card">
							<span class="why-icon" aria-hidden="true">🔬</span>
							<h3>{t('why.deterministic.title')}</h3>
							<p>{t('why.deterministic.body')}</p>
						</div>
						<div class="why-card">
							<span class="why-icon" aria-hidden="true">📍</span>
							<h3>{t('why.linked.title')}</h3>
							<p>{t('why.linked.body')}</p>
						</div>
					</div>
				</section>

				<section class="privacy-band" id="privacy" aria-labelledby="privacy-band-title">
					<div class="band-copy">
						<h2 id="privacy-band-title">{t('band.title')}</h2>
						<p>{t('band.body')}</p>
						<a class="band-link" href="https://github.com/picohook/repcheck#readme" target="_blank" rel="noreferrer">
							{t('band.link')} <span aria-hidden="true">→</span>
						</a>
					</div>
					<div class="band-art" aria-hidden="true">
						<svg viewBox="0 0 260 170" width="260" height="170">
							<rect
								x="42"
								y="18"
								width="176"
								height="116"
								rx="6"
								fill="none"
								stroke="currentColor"
								stroke-width="2.4"
							/>
							<path d="M22 146h216l-10 12H32z" fill="none" stroke="currentColor" stroke-width="2.4" />
							<path d="M50 142h160" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" />
							<path
								d="M116 34h44l16 16v48h-60V34z"
								fill="none"
								stroke="currentColor"
								stroke-width="2.2"
							/>
							<path d="M160 34v16h16" fill="none" stroke="currentColor" stroke-width="2.2" />
							<path d="M124 62h44M124 72h44M124 82h30" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
						</svg>
						<span>{t('band.device')}</span>
					</div>
				</section>

				<section class="toolkit" id="method" aria-labelledby="toolkit-title">
					<div class="toolkit-head">
						<h2 id="toolkit-title">{t('toolkit.title')}</h2>
						<div class="toolkit-chips">
							<span class="chip active">{t('toolkit.articles')}</span>
							<span class="chip">{t('toolkit.theses')}</span>
							<span class="chip">{t('toolkit.proposals')}</span>
						</div>
					</div>
					<div class="toolkit-grid">
						<a
							class="toolkit-card card"
							href="https://github.com/picohook/repcheck/blob/main/docs/METHOD.md"
							target="_blank"
							rel="noreferrer"
						>
							<span class="toolkit-icon" aria-hidden="true">▤</span>
							<div>
								<h3>{t('toolkit.method.title')}</h3>
								<p>{t('toolkit.method.body')}</p>
								<span class="toolkit-link">{t('toolkit.method.link')} →</span>
							</div>
						</a>
						<a
							class="toolkit-card card"
							href="https://github.com/picohook/repcheck#limitations"
							target="_blank"
							rel="noreferrer"
						>
							<span class="toolkit-icon" aria-hidden="true">⚖</span>
							<div>
								<h3>{t('toolkit.limits.title')}</h3>
								<p>{t('toolkit.limits.body')}</p>
								<span class="toolkit-link">{t('toolkit.limits.link')} →</span>
							</div>
						</a>
					</div>
				</section>

				{#if isDev}
					<section id="test-set" class="test-set">
						<TestSetPanel />
					</section>
				{/if}
			{/if}
		</main>

		<footer class="footer">
			<div class="wrap footer-inner">
				<div class="footer-brand">
					<span class="logo footer-logo" aria-hidden="true">R<span class="logo-dot"></span></span>
					<span class="brand-name">RepCheck</span>
					<span class="footer-tag">{t('footer.claim')}</span>
				</div>
				<nav class="footer-nav" aria-label={t('footer.about')}>
					<a href="#method">{t('footer.about')}</a>
					<a href="#privacy">{t('footer.privacy')}</a>
					<a href="https://github.com/picohook/repcheck/issues" target="_blank" rel="noreferrer">
						{t('footer.contact')}
					</a>
				</nav>
			</div>
		</footer>

	</div>
{/if}

{#if limitOpen}
	<UpgradeModal onclose={() => (limitOpen = false)} />
{/if}

<style>
	.shell {
		min-height: 100vh;
		display: flex;
		flex-direction: column;
	}
	.topbar {
		display: flex;
		align-items: center;
		gap: 1.4rem;
		padding: 0.8rem clamp(1rem, 3vw, 2.25rem);
		border-bottom: 1px solid var(--line);
		background: rgb(250 248 244 / 0.92);
		backdrop-filter: blur(8px);
		position: sticky;
		top: 0;
		z-index: 30;
	}
	.brand {
		display: inline-flex;
		align-items: center;
		gap: 0.55rem;
		color: var(--ink);
		text-decoration: none;
		flex: none;
	}
	.logo {
		position: relative;
		display: grid;
		place-items: center;
		width: 30px;
		height: 30px;
		border-radius: 8px;
		background: var(--ink);
		color: var(--paper);
		font-family: var(--font-display);
		font-size: 19px;
		line-height: 1;
		font-style: italic;
		padding-bottom: 2px;
	}
	.logo-dot {
		position: absolute;
		right: 4px;
		bottom: 4px;
		width: 5px;
		height: 5px;
		border-radius: 50%;
		background: #14b8a6;
	}
	.brand-name {
		font-family: var(--font-display);
		font-size: 1.3rem;
		letter-spacing: -0.01em;
	}
	.top-nav {
		display: flex;
		align-items: center;
		gap: 1.35rem;
		margin-right: auto;
	}
	.top-nav a {
		color: var(--ink-2);
		font-size: 0.78rem;
		font-weight: 500;
		text-decoration: none;
		white-space: nowrap;
	}
	.top-nav a:hover {
		color: var(--accent-ink);
	}
	.top-actions {
		display: flex;
		align-items: center;
		gap: 0.4rem;
		margin-left: auto;
	}
	.auth-user {
		max-width: 12rem;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		font-size: 0.72rem;
		color: var(--muted);
	}
	.plan {
		margin-left: 0.3rem;
		padding: 0.05rem 0.3rem;
		border-radius: 99px;
		background: var(--line);
		color: var(--ink-2);
		font-size: 0.64rem;
		text-transform: uppercase;
	}
	.main {
		flex: 1;
		width: 100%;
		max-width: var(--maxw);
		margin-inline: auto;
		padding: 0 clamp(1rem, 3vw, 2.25rem) 1.5rem;
	}

	.hero {
		display: grid;
		grid-template-columns: minmax(0, 1.03fr) minmax(0, 0.97fr);
		gap: clamp(1.6rem, 4vw, 2.8rem);
		align-items: start;
		text-align: left;
		padding: 2.6rem 0 1.8rem;
	}
	.hero-copy {
		min-width: 0;
	}
	.eyebrow {
		margin: 0 0 0.8rem;
		color: var(--accent-ink);
		font-size: 0.68rem;
		font-weight: 700;
		letter-spacing: 0.12em;
		text-transform: uppercase;
	}
	.hero h1 {
		margin: 0 0 0.9rem;
		max-width: 13ch;
		font-size: clamp(2.4rem, 4.1vw, 3.7rem);
		line-height: 1.01;
		letter-spacing: -0.035em;
		text-wrap: balance;
	}
	.lede {
		max-width: 50ch;
		margin: 0 0 1rem;
		font-size: 1rem;
		color: var(--ink-2);
	}
	.privacy-note {
		display: flex;
		align-items: flex-start;
		gap: 0.65rem;
		max-width: 560px;
		margin: 0 0 1rem;
		padding: 0.7rem 0.85rem;
		border: 1px solid #c7e3dd;
		border-radius: 9px;
		background: var(--accent-soft);
	}
	.privacy-icon {
		color: var(--accent-ink);
		display: flex;
		padding-top: 0.1rem;
	}
	.privacy-note strong {
		display: block;
		font-size: 0.8rem;
	}
	.privacy-note span {
		display: block;
		margin-top: 0.12rem;
		font-size: 0.72rem;
		color: var(--ink-2);
	}

	.upload-card {
		max-width: 570px;
		background: var(--card);
		border: 1px solid var(--line);
		border-radius: 13px;
		box-shadow: 0 14px 35px rgb(28 25 23 / 0.06);
		overflow: hidden;
	}
	.upload-tabs {
		display: flex;
		gap: 0.2rem;
		padding: 0 0.85rem;
		border-bottom: 1px solid var(--line);
		background: #fff;
	}
	.upload-tab {
		border: 0;
		border-bottom: 2px solid transparent;
		background: transparent;
		padding: 0.72rem 0.5rem;
		margin-bottom: -1px;
		color: var(--muted);
		font: inherit;
		font-size: 0.76rem;
		font-weight: 600;
		cursor: pointer;
		min-height: 44px;
	}
	.upload-tab.active {
		color: var(--ink);
		border-color: var(--accent-ink);
	}
	.upload-body {
		padding: 0.85rem;
	}
	.upload-body :global(.dropzone) {
		padding: 1.9rem 1rem;
		border-radius: 10px;
	}
	.paste-link {
		display: block;
		margin: 0.5rem auto 0;
		border: 0;
		background: transparent;
		color: var(--accent-ink);
		font: inherit;
		font-size: 0.74rem;
		font-weight: 600;
		text-decoration: underline;
		cursor: pointer;
		min-height: 32px;
	}
	.journal-details {
		margin-top: 0.55rem;
		padding-top: 0.55rem;
		border-top: 1px solid var(--line);
	}
	.journal-details summary {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 0.5rem;
		padding: 0.35rem 0;
		color: var(--ink-2);
		font-size: 0.76rem;
		cursor: pointer;
		list-style: none;
	}
	.journal-details summary::-webkit-details-marker {
		display: none;
	}
	.journal-details[open] summary {
		margin-bottom: 0.55rem;
	}
	.sample-cta {
		display: flex;
		align-items: center;
		gap: 0.7rem;
		width: 100%;
		max-width: 570px;
		padding: 0.8rem 1rem;
		margin-top: 0.6rem;
		border: 1.5px solid var(--accent-ink);
		border-radius: 8px;
		background: transparent;
		color: var(--accent-ink);
		font: inherit;
		text-align: left;
		cursor: pointer;
		transition:
			background 0.15s,
			transform 0.1s;
	}
	.sample-cta:hover:not(:disabled) {
		background: var(--accent-soft);
		transform: translateY(-1px);
	}
	.sample-cta:disabled {
		opacity: 0.55;
		cursor: default;
	}
	.sample-cta strong {
		display: block;
		font-size: 0.82rem;
	}
	.sample-cta small {
		display: block;
		margin-top: 0.1rem;
		color: var(--ink-2);
		font-size: 0.68rem;
	}
	.sample-cta-icon {
		font-size: 1.3rem;
	}
	.sample-cta-arrow {
		margin-left: auto;
		font-size: 1.1rem;
	}

	.sample-report {
		padding: 1rem;
		border-radius: 13px;
		background:
			radial-gradient(120% 80% at 100% 0%, rgb(227 241 238 / 0.8), transparent 62%),
			var(--card);
		box-shadow: 0 18px 45px rgb(28 25 23 / 0.07);
	}
	.sample-kicker {
		display: inline-block;
		margin: 0 0 0.45rem;
		padding: 0.18rem 0.5rem;
		border-radius: 99px;
		background: var(--accent-soft);
		color: var(--accent-ink);
		font-size: 0.6rem;
		font-weight: 800;
		letter-spacing: 0.1em;
		text-transform: uppercase;
	}
	.sample-report h2 {
		margin: 0 0 0.65rem;
		font-size: 1.45rem;
	}
	.sample-tabs {
		display: flex;
		gap: 1rem;
		border-bottom: 1px solid var(--line);
		margin-bottom: 0.65rem;
	}
	.sample-tab {
		border: 0;
		border-bottom: 2px solid transparent;
		background: none;
		padding: 0.45rem 0;
		margin-bottom: -1px;
		color: var(--muted);
		font: inherit;
		font-size: 0.7rem;
		cursor: pointer;
	}
	.sample-tab.active {
		color: var(--accent-ink);
		border-color: var(--accent-ink);
		font-weight: 700;
	}
	.finding-alert {
		display: flex;
		align-items: center;
		gap: 0.4rem;
		flex-wrap: wrap;
		padding: 0.55rem 0.65rem;
		border: 1px solid #d9a441;
		border-radius: 8px;
		background: var(--warning-soft);
	}
	.finding-alert strong {
		font-size: 0.78rem;
	}
	.finding-icon {
		display: grid;
		place-items: center;
		width: 19px;
		height: 19px;
		border-radius: 50%;
		border: 1px solid #b87912;
		color: #9a5b06;
		font-size: 0.66rem;
		font-weight: 800;
	}
	.finding-confidence {
		margin-left: auto;
		padding: 0.12rem 0.42rem;
		border-radius: 99px;
		background: #fff;
		color: #7a4a05;
		font-size: 0.58rem;
		font-weight: 700;
		white-space: nowrap;
	}
	.sample-label {
		margin: 0.55rem 0 0.25rem;
		color: var(--muted);
		font-size: 0.64rem;
	}
	.sample-excerpt {
		padding: 0.5rem 0.6rem;
		border: 1px solid var(--line);
		border-radius: 6px;
		background: #fff;
		font-size: 0.8rem;
	}
	.sample-excerpt mark {
		padding: 0 0.12rem;
		border-radius: 2px;
		background: #fde68a;
		color: inherit;
	}
	.p-compare {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 0.45rem;
		margin-top: 0.45rem;
	}
	.p-compare div {
		padding: 0.45rem 0.55rem;
		border: 1px solid var(--line);
		border-radius: 7px;
		background: #f7f4ee;
	}
	.p-compare span {
		display: block;
		color: var(--muted);
		font-size: 0.6rem;
	}
	.p-compare strong {
		display: block;
		margin-top: 0.1rem;
		font-family: var(--font-display);
		font-size: 1.05rem;
		line-height: 1.1;
	}
	.sample-explanation {
		margin-top: 0.55rem;
	}
	.sample-explanation h3,
	.evidence-box h3 {
		margin: 0 0 0.2rem;
		font-family: var(--font-ui);
		font-size: 0.72rem;
		font-weight: 700;
	}
	.sample-explanation p {
		margin: 0;
		color: var(--ink-2);
		font-size: 0.7rem;
		line-height: 1.4;
	}
	.source-link {
		display: inline-block;
		margin-top: 0.3rem;
		padding: 0;
		border: 0;
		background: none;
		color: var(--accent-ink);
		font: inherit;
		font-size: 0.68rem;
		font-weight: 650;
		cursor: pointer;
	}
	.evidence-box {
		margin-top: 0.6rem;
		padding: 0.55rem 0.65rem;
		border: 1px solid #c7e3dd;
		border-radius: 8px;
		background: var(--accent-soft);
	}
	.evidence-box p {
		margin: 0;
		color: var(--ink-2);
		font-size: 0.68rem;
		line-height: 1.45;
	}
	.sample-note {
		margin: 0.5rem 0 0;
		color: var(--muted);
		font-size: 0.6rem;
		font-style: italic;
	}

	.metrics {
		padding: 1.6rem 0;
		border-bottom: 1px solid var(--line);
	}
	.metric-row {
		display: grid;
		grid-template-columns: repeat(4, 1fr);
		gap: 1rem;
		margin: 0;
		padding: 0;
		list-style: none;
		text-align: center;
	}
	.metric-num {
		display: block;
		color: var(--accent-ink);
		font-family: var(--font-display);
		font-size: clamp(1.8rem, 3vw, 2.4rem);
		font-weight: 700;
	}
	.metric-text {
		display: block;
		margin-top: 0.2rem;
		color: var(--ink-2);
		font-size: 0.72rem;
	}

	.checks {
		padding: 1.4rem 0 1.2rem;
		scroll-margin-top: 90px;
	}
	.section-head {
		text-align: left;
		margin-bottom: 1rem;
	}
	.section-head h2 {
		margin: 0;
		font-size: clamp(1.8rem, 3vw, 2.45rem);
		letter-spacing: -0.02em;
	}
	.check-grid {
		list-style: none;
		padding: 0;
		margin: 0;
		display: grid;
		grid-template-columns: repeat(3, minmax(0, 1fr));
		gap: 0.65rem;
	}
	.check {
		display: flex;
		flex-direction: row;
		align-items: flex-start;
		gap: 0.7rem;
		padding: 0.95rem;
		border-radius: 10px;
	}
	.check-icon {
		display: grid;
		place-items: center;
		flex: none;
		width: 34px;
		height: 34px;
		border: 0;
		border-radius: 8px;
		background: transparent;
		color: var(--accent-ink);
		font-size: 1.25rem;
		font-weight: 700;
	}
	.check h3 {
		margin: 0 0 0.22rem;
		font-family: var(--font-ui);
		font-size: 0.82rem;
		font-weight: 650;
	}
	.check p {
		margin: 0;
		color: var(--ink-2);
		font-size: 0.73rem;
		line-height: 1.42;
	}
	.check-detail {
		margin: 0.3rem 0 0;
		color: var(--ink-2);
		font-size: 0.65rem;
		letter-spacing: 0.01em;
		opacity: 0.75;
	}

	.why {
		padding: 1.6rem 0;
		border-top: 1px solid var(--line);
	}
	.why h2 {
		margin: 0 0 1rem;
		font-size: clamp(1.4rem, 2.5vw, 1.8rem);
	}
	.why-grid {
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: 1rem;
	}
	.why-card {
		padding: 1.2rem;
		border-radius: 10px;
		background: var(--accent-soft);
	}
	.why-icon {
		display: block;
		margin-bottom: 0.5rem;
		font-size: 1.4rem;
	}
	.why-card h3 {
		margin: 0 0 0.35rem;
		font-family: var(--font-ui);
		font-size: 0.82rem;
		font-weight: 700;
	}
	.why-card p {
		margin: 0;
		color: var(--ink-2);
		font-size: 0.74rem;
		line-height: 1.5;
	}

	.privacy-band {
		display: grid;
		grid-template-columns: minmax(0, 1.15fr) minmax(0, 0.85fr);
		gap: 1.5rem;
		align-items: center;
		margin: 1.2rem 0 1.6rem;
		padding: clamp(1.2rem, 3vw, 1.9rem);
		border: 1px solid #c7e3dd;
		border-radius: 13px;
		background: var(--accent-soft);
		scroll-margin-top: 90px;
	}
	.band-copy h2 {
		margin: 0 0 0.45rem;
		max-width: 24ch;
		font-size: clamp(1.45rem, 2.4vw, 1.9rem);
		line-height: 1.12;
	}
	.band-copy p {
		margin: 0 0 0.65rem;
		max-width: 52ch;
		color: var(--ink-2);
		font-size: 0.8rem;
	}
	.band-link {
		color: var(--accent-ink);
		font-size: 0.76rem;
		font-weight: 700;
		text-decoration: underline;
	}
	.band-art {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 0.25rem;
		padding-left: 1.2rem;
		border-left: 1px solid #b9dcd4;
		color: var(--accent-ink);
	}
	.band-art span {
		font-size: 0.72rem;
		font-weight: 650;
		color: var(--ink-2);
	}

	.toolkit {
		padding: 0.2rem 0 1.5rem;
		scroll-margin-top: 90px;
	}
	.toolkit-head {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 1rem;
		flex-wrap: wrap;
		margin-bottom: 0.85rem;
	}
	.toolkit-head h2 {
		margin: 0;
		font-size: clamp(1.55rem, 2.5vw, 2rem);
		letter-spacing: -0.02em;
	}
	.toolkit-chips {
		display: flex;
		flex-wrap: wrap;
		gap: 0.35rem;
	}
	.toolkit-chips .chip {
		padding: 0.28rem 0.6rem;
		border: 1px solid var(--line);
		border-radius: 99px;
		background: #fff;
		color: var(--muted);
		font-size: 0.65rem;
		font-weight: 600;
	}
	.toolkit-chips .chip.active {
		border-color: #b9dcd4;
		background: var(--accent-soft);
		color: var(--accent-ink);
	}
	.toolkit-grid {
		display: grid;
		grid-template-columns: 1fr 1fr;
		gap: 0.65rem;
	}
	.toolkit-card {
		display: flex;
		gap: 0.75rem;
		align-items: flex-start;
		padding: 0.95rem;
		color: var(--ink);
		text-decoration: none;
		border-radius: 10px;
	}
	.toolkit-card:hover {
		border-color: #b9dcd4;
	}
	.toolkit-icon {
		flex: none;
		display: grid;
		place-items: center;
		width: 36px;
		height: 36px;
		border: 1px solid #c7e3dd;
		border-radius: 8px;
		color: var(--accent-ink);
		font-size: 1.05rem;
	}
	.toolkit-card h3 {
		margin: 0 0 0.2rem;
		font-family: var(--font-ui);
		font-size: 0.82rem;
	}
	.toolkit-card p {
		margin: 0 0 0.3rem;
		color: var(--ink-2);
		font-size: 0.73rem;
		line-height: 1.42;
	}
	.toolkit-link {
		color: var(--accent-ink);
		font-size: 0.7rem;
		font-weight: 650;
	}

	.batch-view {
		max-width: 860px;
		margin: 2rem auto 3rem;
	}
	.batch-view .upload-tabs {
		border: 1px solid var(--line);
		border-radius: 9px;
		background: #fff;
		padding: 0 0.4rem;
	}
	.batch-panel {
		margin-top: 1rem;
	}
	.journal-row {
		display: flex;
		margin: 0.2rem 0 1rem;
	}

	.processing-wrap {
		margin-top: 1.2rem;
	}
	.error-card {
		margin: 1.2rem 0 0;
		max-width: 560px;
		padding: 1.1rem 1.2rem;
		text-align: left;
		border-left: 3px solid var(--critical);
	}
	.error-card h2 {
		margin: 0 0 0.35rem;
		font-family: var(--font-ui);
		font-size: 1rem;
		font-weight: 650;
	}
	.error-card p {
		margin: 0 0 0.5rem;
		color: var(--ink-2);
		font-size: 0.86rem;
	}
	.row {
		display: flex;
		gap: 0.5rem;
		flex-wrap: wrap;
	}
	.row-between {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 0.6rem;
	}
	.paste {
		margin: 1rem 0 0;
		max-width: 660px;
		padding: 1rem 1.1rem;
		text-align: left;
	}
	.paste h2 {
		margin: 0;
		font-family: var(--font-ui);
		font-size: 1rem;
		font-weight: 650;
	}
	.paste textarea {
		width: 100%;
		margin: 0.6rem 0;
		padding: 0.7rem 0.8rem;
		border: 1px solid var(--line-strong);
		border-radius: 9px;
		background: #fff;
		font-family: var(--font-mono);
		font-size: 0.82rem;
		line-height: 1.55;
		resize: vertical;
	}

	.footer {
		border-top: 1px solid var(--line);
		margin-top: 0.5rem;
		padding: 1.1rem 0;
	}
	.footer-inner {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 1rem;
		flex-wrap: wrap;
	}
	.footer-brand {
		display: flex;
		align-items: center;
		gap: 0.5rem;
	}
	.footer-logo {
		width: 24px;
		height: 24px;
		font-size: 14px;
		border-radius: 6px;
	}
	.footer-tag {
		padding-left: 0.6rem;
		border-left: 1px solid var(--line);
		color: var(--muted);
		font-size: 0.7rem;
	}
	.footer-nav {
		display: flex;
		gap: 1.1rem;
	}
	.footer-nav a {
		color: var(--ink-2);
		font-size: 0.72rem;
		text-decoration: none;
	}
	.footer-nav a:hover {
		color: var(--accent-ink);
	}

	.test-set {
		margin-top: 1.5rem;
	}

	@media (max-width: 820px) {
		.top-nav {
			display: none;
		}
		.hero {
			grid-template-columns: 1fr;
			gap: 1.3rem;
			padding-top: 1.6rem;
		}
		.hero-copy {
			order: 1;
		}
		.sample-report {
			order: 2;
		}
		.hero h1 {
			max-width: 16ch;
		}

		.check-grid {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}
		.privacy-band {
			grid-template-columns: 1fr;
		}
		.band-art {
			border-left: 0;
			border-top: 1px solid #b9dcd4;
			padding: 0.8rem 0 0;
		}
		.toolkit-grid {
			grid-template-columns: 1fr;
		}
	}
	@media (max-width: 620px) {
		.hero h1 {
			font-size: 2.15rem;
			max-width: none;
		}
		.check-grid {
			grid-template-columns: 1fr;
		}
		.metric-row {
			grid-template-columns: repeat(2, 1fr);
			gap: 1.2rem 0.5rem;
		}
		.why-grid {
			grid-template-columns: 1fr;
		}
		.topbar {
			flex-wrap: wrap;
			gap: 0.5rem;
			padding-inline: 0.85rem;
		}
		.top-actions {
			width: 100%;
			justify-content: flex-end;
			flex-wrap: wrap;
		}
		.upload-body {
			padding: 0.65rem;
		}
		.upload-body :global(.dropzone) {
			padding: 1.5rem 0.7rem;
		}
		.sample-report {
			padding: 0.85rem;
		}
		.footer-inner {
			align-items: flex-start;
			flex-direction: column;
		}
		.footer-tag {
			border-left: 0;
			padding-left: 0;
			flex-basis: 100%;
		}
	}
</style>

